import React, { useState } from "react";

import camelCase from "lodash/camelCase";
import PropTypes from "prop-types";

import { Button, Modal, Text } from "components/ui/source";

import styles from "./AdaAccessibility.module.scss";

function AdaAccessibilityModal({
  isOpen,
  closePopup,
  accessibilityType,
  features,
}) {
  const prefix = "listing:availability.collection.adaAccessibility";

  const modalProps = {
    isOpen: isOpen,
    onRequestClose: closePopup,
    shouldCloseOnOverlayClick: true,
    shouldCloseOnEsc: true,
    className: styles.popup_container,
  };

  const accessibilityElements = features.map(textKey => (
    <li className={styles.list} key={textKey}>
      <Text
        types={["body1"]}
        translateKey={`${prefix}.accessibleFeatures.${camelCase(textKey)}`}
      />
    </li>
  ));

  return (
    <Modal modalProps={modalProps}>
      <Modal.Close onClick={closePopup} />
      <Modal.Content className={styles.content_container}>
        <Text
          className={styles.title}
          types={["body1", "medium"]}
          translateKey={`${prefix}.${accessibilityType}ModalTitle`}
        />
        <ul>{accessibilityElements}</ul>
      </Modal.Content>
    </Modal>
  );
}

AdaAccessibilityModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  closePopup: PropTypes.func.isRequired,
  features: PropTypes.arrayOf(PropTypes.string),
  accessibilityType: PropTypes.string.isRequired,
};

AdaAccessibilityModal.defaultProps = {
  isOpen: false,
  closePopup: () => {},
  features: [],
};

function AdaAccessibilitySection({ accessibilityType, className, features }) {
  const [overlayVisible, setOverlayVisible] = useState(false);
  const prefix = "listing:availability.collection.adaAccessibility";

  const openPopup = () => {
    setOverlayVisible(true);
  };

  const closePopup = () => {
    setOverlayVisible(false);
  };

  return (
    <li className={className}>
      {features.length > 0 ? (
        <Button
          textProps={{
            types: ["body1", "underline"],
            translateKey: `${prefix}.${accessibilityType}AccessibleMessage`,
          }}
          className={styles.link_button}
          centered={false}
          type="no_background"
          onClick={openPopup}
        />
      ) : (
        <Text
          types={["body1", "underline"]}
          translateKey={`${prefix}.${accessibilityType}AccessibleMessage`}
        />
      )}

      <AdaAccessibilityModal
        isOpen={overlayVisible}
        closePopup={closePopup}
        accessibilityType={accessibilityType}
        features={features}
      />
    </li>
  );
}

AdaAccessibilitySection.propTypes = {
  accessibilityType: PropTypes.string,
  className: PropTypes.string,
  features: PropTypes.arrayOf(PropTypes.string),
};

AdaAccessibilitySection.defaultProps = {
  features: [],
};

function AdaAccessibility({
  className,
  isHearingAccessible,
  isMobilityAccessible,
  hearingFeatures,
  mobilityFeatures,
}) {
  if (!isHearingAccessible && !isMobilityAccessible) return null;
  return (
    <>
      {isHearingAccessible && (
        <AdaAccessibilitySection
          className={className}
          accessibilityType="hearing"
          features={hearingFeatures}
        />
      )}
      {isMobilityAccessible && (
        <AdaAccessibilitySection
          className={className}
          accessibilityType="mobility"
          features={mobilityFeatures}
        />
      )}
    </>
  );
}

AdaAccessibility.propTypes = {
  className: PropTypes.string,
  isHearingAccessible: PropTypes.bool,
  isMobilityAccessible: PropTypes.bool,
  hearingFeatures: PropTypes.arrayOf(PropTypes.string),
  mobilityFeatures: PropTypes.arrayOf(PropTypes.string),
};

AdaAccessibility.defaultProps = {
  isHearingAccessible: false,
  isMobilityAccessible: false,
  hearingFeatures: [],
  mobilityFeatures: [],
};

export default AdaAccessibility;
